import { motion } from "framer-motion";
import { FileText, FlaskConical, Brush, CalendarClock } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { IMAGES } from "@/content/site";

const STEPS = [
  {
    icon: CalendarClock,
    title: "Startup scheduling & fill-day coordination",
    text: "We coordinate with your builder or plaster crew so the water, chemistry, and brushing schedule start on day one — the most important day of your pool's life.",
  },
  {
    icon: FlaskConical,
    title: "Careful chemistry management",
    text: "New plaster and fresh surfaces cure for weeks. We test and adjust methodically to protect the finish — no aggressive shortcuts that cause etching, scaling, or discoloration.",
  },
  {
    icon: Brush,
    title: "Scheduled brushing & monitoring",
    text: "New surfaces need frequent brushing to remove plaster dust and help the finish cure evenly. We handle the schedule so nothing gets missed.",
  },
  {
    icon: FileText,
    title: "Professional documentation",
    text: "You receive detailed startup documentation and water chemistry reports — records that protect your warranty and give your builder confidence the startup was done right.",
  },
];

export default function StartupsPage() {
  return (
    <>
      <SEO
        title="New Pool & Pool Surface Startup Service in Pinellas County FL | Point of Pines"
        description="Professional new pool and new surface startups across Pinellas County, FL. Careful curing chemistry, scheduled brushing, and documented water chemistry reporting. Call (727) 744-5908."
        path="/pool-startups/"
      />
      <PageHero
        overline="New Pool & Surface Startups — Pinellas County, FL"
        title="New Pool Startups"
        accent="With Full Documentation"
        description="A new pool or fresh surface is a major investment — and the first few weeks decide how it looks for years. We manage the startup process carefully and document every step."
        image={IMAGES.startupPool}
        imageAlt="Newly started up residential pool with brilliant blue water in Pinellas County Florida"
      />

      <section data-testid="startup-steps-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">The Startup Process</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              The first weeks decide the next decade
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Fresh plaster and new finishes are vulnerable while they cure. Rushed chemistry or a
              missed brushing schedule can permanently mark a brand-new surface. Our startup service
              follows a careful, documented process from fill day forward.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                data-testid={`startup-step-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
                    <s.icon className="w-6 h-6 text-sky2" />
                  </span>
                  <span className="font-mono text-sm text-pool">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy tracking-tight leading-snug">{s.title}</h3>
                <p className="mt-3 text-sm sm:text-[15px] text-slate-500 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="startup-documentation-section" className="py-16 sm:py-24 bg-navy text-white relative overflow-hidden">
        <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold">Documentation & Reporting</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
              Records your builder will respect
            </h2>
            <p className="mt-5 text-base text-slate-300 leading-relaxed">
              Every startup includes professional documentation: dated water chemistry readings,
              adjustments made, and brushing milestones. If a warranty question ever comes up,
              you have a clear record that the surface was started and cared for correctly.
            </p>
            <p className="mt-4 text-base text-slate-300 leading-relaxed">
              Building or resurfacing a pool in Clearwater, St. Petersburg, Palm Harbor, or anywhere
              in Pinellas County? Talk to us before the water truck arrives.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="clip-arch overflow-hidden ring-1 ring-white/15 shadow-2xl shadow-black/30">
              <img
                src={IMAGES.waterTesting}
                alt="Documented pool water chemistry testing during a new pool surface startup in Pinellas County"
                className="w-full h-72 sm:h-96 object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Building or resurfacing a pool?"
        text="Schedule your startup before the fill. We'll handle the chemistry, the brushing, and the paperwork."
      />
    </>
  );
}
